import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChatCircleDots, ArrowSquareOut, ArrowUp, ChatCircle, Users, MagicWand, CaretDown, Hammer } from "@phosphor-icons/react";
import { Search } from "lucide-react";
import GuideHelpButton from "../components/GuideHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;

const VERDICT = {
  emerging: { label: "💎 Emerging", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  competitive: { label: "Competitive", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  saturated: { label: "🔴 Saturated", cls: "bg-red-50 text-red-600 ring-red-200" },
  whitespace: { label: "💎 Whitespace", cls: "bg-violet-50 text-violet-700 ring-violet-200" },
  underserved: { label: "🟢 Underserved", cls: "bg-teal-50 text-teal-700 ring-teal-200" },
  unvalidated: { label: "Unvalidated", cls: "bg-zinc-100 text-zinc-500 ring-zinc-200" },
  brand: { label: "Brand", cls: "bg-zinc-100 text-zinc-500 ring-zinc-200" },
};

const Related = ({ p, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const nx = !open;
    setOpen(nx);
    if (nx && !data) {
      setLoading(true);
      axios.get(`${API}/pain-points/${p.id}/related`)
        .then(r => setData(r.data)).catch(() => setData({ markets: [], models: [] }))
        .finally(() => setLoading(false));
    }
  };

  const hasAny = data && ((data.markets || []).length || (data.models || []).length);

  return (
    <div className="mt-3 pt-3 border-t border-zinc-100" onClick={e => e.stopPropagation()}>
      <button onClick={toggle} data-testid={`pain-related-toggle-${p.id}`}
        className="inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:text-violet-900 transition-colors">
        <MagicWand size={13} weight="duotone" /> Related markets &amp; proven models
        <CaretDown size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2.5 space-y-2.5" data-testid={`pain-related-${p.id}`}>
          {loading && <div className="text-[11px] text-zinc-400 animate-pulse">Matching against demand + competition data…</div>}
          {!loading && !hasAny && (
            <div className="text-[11px] text-zinc-400">No direct market match yet — <button onClick={() => onNavigate?.("discover")} className="text-violet-600 underline">explore Discover →</button></div>
          )}
          {!loading && (data.markets || []).length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Related demand markets · their competition</div>
              <div className="flex flex-wrap gap-1.5">
                {data.markets.map((m, i) => {
                  const v = VERDICT[m.verdict] || VERDICT.unvalidated;
                  return (
                    <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-white border border-zinc-200 rounded-lg px-2 py-1">
                      <span className="font-medium text-zinc-700">{m.name}</span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ring-1 ${v.cls}`}>{v.label}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {!loading && (data.models || []).length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Proven models you could clone</div>
              <div className="flex flex-wrap gap-1.5">
                {data.models.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-violet-50 border border-violet-200 rounded-lg px-2 py-1 text-violet-800">
                    {m.cluster_label || m.centroid_name}
                    {m.gap_score != null && <span className="text-[9px] font-mono text-violet-500">gap {Number(m.gap_score).toFixed(1)}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {!loading && hasAny && (
            <button onClick={() => onNavigate?.((data.models || []).length ? "discover" : "trending")} data-testid={`pain-related-cta-${p.id}`}
              className="text-[11px] font-semibold text-violet-700 hover:underline inline-flex items-center gap-1">
              Open in {(data.models || []).length ? "Discover" : "Trending"} <ArrowSquareOut size={11} weight="bold" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Card = ({ p, onNavigate }) => (
  <div className="group block bg-white border border-zinc-200 rounded-xl p-4 hover:border-violet-300 hover:shadow-md transition-all" data-testid={`pain-card-${p.id}`}>
    <a href={p.url || "#"} target="_blank" rel="noopener noreferrer" className="block">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">{p.category_icon} {p.category}</span>
        {p.is_ai && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">AI</span>}
        {p.is_new && <span data-testid={`pain-new-${p.id}`} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">New</span>}
      </div>
      <h3 className="font-semibold text-zinc-900 text-[15px] leading-snug">{p.problem}</h3>
      {p.target && (
        <div className="flex items-center gap-1 mt-1.5 text-[12px] text-violet-700 font-medium">
          <Users size={13} weight="duotone" /> for {p.target}
        </div>
      )}
      {p.evidence && (
        <p className="text-[12px] text-zinc-500 italic mt-2 leading-relaxed line-clamp-3 border-l-2 border-zinc-200 pl-2">“{p.evidence}”</p>
      )}
      {p.served && (
        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-2 py-1" data-testid={`pain-served-${p.id}`}>
          ⚠ Likely already served{p.competitors?.length ? <> — e.g. <span className="font-semibold">{p.competitors.join(", ")}</span></> : ""}
        </div>
      )}
      <div className="flex items-center gap-3 mt-3 text-[11px] font-mono text-zinc-400">
        <span className="font-mono">r/{p.subreddit}</span>
        {p.captured_days_ago != null && (
          <span title={`Captured ${p.captured_at}`} data-testid={`pain-captured-${p.id}`}>
            {p.captured_days_ago === 0 ? "captured today" : `captured ${p.captured_days_ago}d ago`}
          </span>
        )}
        {p.score != null && <span className="inline-flex items-center gap-1" title="Real upvotes on the original Reddit post"><ArrowUp size={12} weight="bold" /> {p.score.toLocaleString()}</span>}
        {p.comments != null && <span className="inline-flex items-center gap-1" title="Comments on the original Reddit post"><ChatCircle size={12} weight="bold" /> {p.comments.toLocaleString()}</span>}
        <span className="ml-auto inline-flex items-center gap-1 text-violet-600 group-hover:underline">View source <ArrowSquareOut size={12} weight="bold" /></span>
      </div>
    </a>
    <Related p={p} onNavigate={onNavigate} />
    <button
      onClick={() => {
        const prompt = `I found a real user problem on Reddit (r/${p.subreddit}): "${p.problem}" — for ${p.target || "a specific audience"}. Evidence from the post: "${p.evidence}". ${p.served ? `Note: this looks already served by tools like ${(p.competitors || []).join(", ") || "existing players"}, so focus on a sharp wedge or underserved segment. ` : ""}Help me scope a focused SaaS to solve this: who exactly is the buyer, the smallest valuable MVP, how I'd reach them, and how to differentiate.`;
        try { sessionStorage.setItem("pp_build_prompt", prompt); } catch (e) {}
        onNavigate?.("copilot");
      }}
      data-testid={`pain-build-${p.id}`}
      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 text-white text-[13px] font-semibold hover:bg-violet-700 transition-colors">
      <Hammer size={14} weight="duotone" /> Build this — ask the Advisor →
    </button>
  </div>
);

const Skeleton = () => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4 animate-pulse" data-testid="pain-skeleton">
    <div className="flex gap-2 mb-2"><div className="h-3 w-24 rounded bg-zinc-200" /></div>
    <div className="h-4 w-5/6 rounded bg-zinc-200 mb-2" />
    <div className="h-3 w-1/3 rounded bg-zinc-100 mb-2" />
    <div className="h-3 w-full rounded bg-zinc-100" />
  </div>
);

export default function PainPoints({ onNavigate }) {
  const [facets, setFacets] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [aiOnly, setAiOnly] = useState(false);
  const [hideServed, setHideServed] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [sort, setSort] = useState("best");
  const [page, setPage] = useState(1);

  const loadFacets = () => axios.get(`${API}/pain-points/facets`).then(r => setFacets(r.data)).catch(() => {});
  useEffect(() => { loadFacets(); }, []);

  const curating = facets?.curation?.running;
  useEffect(() => {
    if (!curating) return;
    const iv = setInterval(loadFacets, 8000);
    return () => clearInterval(iv);
  }, [curating]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (category) p.set("category", category);
    if (aiOnly) p.set("ai_only", "true");
    if (hideServed) p.set("hide_served", "true");
    if (newOnly) p.set("new_only", "true");
    p.set("sort", sort);
    p.set("page", String(page));
    p.set("page_size", String(PAGE_SIZE));
    return p.toString();
  }, [q, category, aiOnly, hideServed, newOnly, sort, page]);

  useEffect(() => { setPage(1); }, [q, category, aiOnly, hideServed, newOnly, sort]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/pain-points?${qs}`)
        .then(r => { if (!cancel) { setItems(r.data.items || []); setTotal(r.data.total || 0); } })
        .catch(() => { if (!cancel) { setItems([]); setTotal(0); } })
        .finally(() => { if (!cancel) setLoading(false); });
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [qs]);

  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-2 sm:px-0" data-testid="pain-points-root">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-violet-700 mb-1">Pain Points · AI-curated</div>
      <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight flex items-center gap-2" data-testid="pain-points-headline">
        {facets ? `${facets.total.toLocaleString()} real problems worth building` : "Real problems worth building"}
        <GuideHelpButton feature="pain-points" />
      </h1>
      <p className="text-sm text-zinc-600 mt-1 max-w-3xl leading-relaxed">
        Real frustrations from Reddit, read by AI and kept only when there's a genuine, buildable software problem underneath. Each card is the problem stated plainly, who has it, and a link to the original thread. The opinions, jokes and brags are filtered out.
      </p>

      {curating && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-3 py-1.5 rounded-full" data-testid="pain-curating">
          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          AI is curating… {facets?.curation?.done?.toLocaleString()} / {facets?.curation?.total?.toLocaleString()} posts read · {facets?.curation?.buildable?.toLocaleString()} real problems found so far
        </div>
      )}

      {/* Category filter pills */}
      {facets?.buckets?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="pain-bucket-pills">
          <button onClick={() => setCategory("")} data-testid="pain-bucket-all"
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${category === "" ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"}`}>
            All problems
          </button>
          {facets.buckets.map(b => (
            <button key={b.value} onClick={() => setCategory(b.value)} data-testid={`pain-bucket-${b.value}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${category === b.value ? "bg-amber-500 text-white" : "bg-white border border-amber-200 text-amber-700 hover:border-amber-400"}`}>
              {b.label} <span className={category === b.value ? "text-amber-100" : "text-amber-400"}>{b.count.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="mt-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search problems (e.g. invoicing, scheduling, onboarding)…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:border-violet-400" data-testid="pain-search" />
        </div>
        <label className="inline-flex items-center gap-1.5 text-[12px] font-mono text-zinc-600 cursor-pointer px-2" data-testid="pain-ai-wrapper">
          <input type="checkbox" checked={aiOnly} onChange={e => setAiOnly(e.target.checked)} data-testid="pain-ai-toggle" />
          <ChatCircleDots size={14} weight="duotone" /> AI niche{facets?.ai_count ? ` (${facets.ai_count.toLocaleString()})` : ""}
        </label>
        <label className="inline-flex items-center gap-1.5 text-[12px] font-mono text-zinc-600 cursor-pointer px-2" data-testid="pain-served-wrapper" title="Hide problems already served by known commercial tools">
          <input type="checkbox" checked={hideServed} onChange={e => setHideServed(e.target.checked)} data-testid="pain-hide-served-toggle" />
          Hide already-served
        </label>
        <label className="inline-flex items-center gap-1.5 text-[12px] font-mono text-zinc-600 cursor-pointer px-2" data-testid="pain-new-wrapper" title="Only problems captured in the last 7 days">
          <input type="checkbox" checked={newOnly} onChange={e => setNewOnly(e.target.checked)} data-testid="pain-new-toggle" />
          New this week{facets?.new_count != null ? ` (${facets.new_count.toLocaleString()})` : ""}
        </label>
        <select value={sort} onChange={e => setSort(e.target.value)} data-testid="pain-sort"
          className="px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm font-medium text-zinc-700 focus:outline-none focus:border-violet-400">
          <option value="best">Best match</option>
          <option value="top">Most upvoted</option>
          <option value="recent">Most recent</option>
        </select>
      </div>

      <div className="text-sm text-zinc-500 mb-3" data-testid="pain-count">{loading ? "Finding problems…" : `${total.toLocaleString()} problems`}</div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pain-grid">
        {loading && items.length === 0
          ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={`psk-${i}`} />)
          : items.map(p => <Card key={p.id} p={p} onNavigate={onNavigate} />)}
      </div>

      {!loading && items.length === 0 && (
        <div className="text-center py-12 text-zinc-500 text-sm" data-testid="pain-empty">
          {curating
            ? "AI is still curating — real problems will appear here as they're verified. Check back in a minute."
            : <>No problems match that. <button onClick={() => { setQ(""); setCategory(""); setAiOnly(false); }} className="text-violet-700 underline">Reset</button>.</>}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-5">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="pain-prev"
            className="text-xs font-mono px-4 py-2 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40">‹ prev</button>
          <span className="text-[11px] font-mono text-zinc-500">page {page} of {pages.toLocaleString()}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} data-testid="pain-next"
            className="text-xs font-mono px-4 py-2 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40">next ›</button>
        </div>
      )}
    </div>
  );
}
